import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import ErrorState from '../../../components/ui/ErrorState';
import { deriveErrorState } from '../../../utils/errorStateUtils';
import { useAuth } from '../../../hooks/useAuth';
import { teamReviewService } from '../../../services/teamReviewService';
import tuneIcon from '../../../assets/tune.svg';
import arrowDropDownIcon from '../../../assets/arrow_drop_down_filter.svg';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import Pagination from '../../../components/ui/Pagination';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';





// Constants
const PageSizeOptions = [10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 10;
const INITIAL_PAGE = 1;
const SEARCH_BAR_WIDTH = '70%';

// Team Review interface
interface TeamReview {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reviewStatus: string; // Using dynamic status from API instead of hardcoded values
  nominationStatus?: string;
  okrStatus?: string;
  employeeEmail: string;
  nominationRelationshipId: string;
  managerEmail?: string;
}

const TeamReviewPageView: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const hasActiveCycle = useActiveCycle();
  const [reviews, setReviews] = useState<TeamReview[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Track if data has been fetched to prevent duplicate API calls
  const hasFetched = React.useRef(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    designation: '',
    reviewStatus: '',
    okrStatus: ''
  });
  const [tempFilters, setTempFilters] = useState({
    department: '',
    designation: '',
    reviewStatus: '',
    okrStatus: ''
  });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  // Fetch team reviews from API
  const fetchTeamReviews = useCallback(async () => {
    if (authLoading || !user?.email) {
      return;
    }
    
    // Prevent duplicate API calls
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    try {
      setLoading(true);
      setError(null);
      setErrorStatus(undefined);

      const response = await teamReviewService.getTeamNominationReviewList(user.email);

      if (response.status && response.data) {
        let reviewList: unknown[] = [];

        if (response.data.nominators && Array.isArray(response.data.nominators)) {
          reviewList = response.data.nominators;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          reviewList = response.data.data;
        } else if (Array.isArray(response.data)) {
          reviewList = response.data;
        }

        interface NominatorApiItem {
          nominator_id?: number | string;
          nomination_id?: number | string;
          employee_id?: string;
          nominator_name?: string;
          nominator_designation?: string;
          nominator_department?: string;
          nominator_manager?: {
            manager_name?: string;
            manager_email?: string;
          };
          review_exists?: boolean;
          nominator_email?: string;
          nomination_status?: string;
          okr_status?: string;
          manager_email?: string;
          nomination_relationship_id?: number | string;
        }

        const transformedReviews: TeamReview[] = (reviewList as NominatorApiItem[]).map((item: NominatorApiItem) => ({
          id: String(item.nominator_id ?? item.nomination_id ?? Math.random()),
          employeeId: item.employee_id || '',
          employeeName: item.nominator_name || '',
          designation: item.nominator_designation || '',
          department: item.nominator_department || '',
          managerName: item.nominator_manager?.manager_name || '',
          reviewStatus: item.review_exists ? 'submitted' : 'pending',
          nominationStatus: item.nomination_status,
          okrStatus: item.okr_status,
          employeeEmail: item.nominator_email || '',
          managerEmail: item.nominator_manager?.manager_email || item.manager_email || '',
          nominationRelationshipId: String(item.nomination_relationship_id ?? item.nominator_id ?? Math.random()),
        }));

        setReviews(transformedReviews);
      } else {
        const message = response?.message || 'Failed to load team reviews. Please try again.';
        setError(message);
        setReviews([]);
      }
    } catch (err) {
      console.error('❌ Error fetching team reviews:', err);

      let statusCode: number | undefined;
      let message = 'Failed to load team reviews. Please try again.';

      if (err && typeof err === 'object') {
        if ('status_code' in err && typeof (err as { status_code?: unknown }).status_code === 'number') {
          statusCode = (err as { status_code: number }).status_code;
        }
        if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
          message = (err as { message: string }).message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setErrorStatus(statusCode);
      setError(message);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, user?.email]);

  useEffect(() => {
    fetchTeamReviews();
  }, [fetchTeamReviews]);

  const handleRetry = () => {
    if (!loading) {
      fetchTeamReviews();
    }
  };

  // Listen for global clearFilters event from SearchFilterBar
  useEffect(() => {
    const handler = () => {
      handleRemoveAllFilters();
    };

    window.addEventListener('clearFilters', handler as EventListener);
    return () => window.removeEventListener('clearFilters', handler as EventListener);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Filter reviews based on search term and filters with useMemo optimization
  const filteredReviews = useMemo(() => {
    return reviews.filter(review => {
      const matchesSearch = !searchTerm || (
        review.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        review.managerName.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesDepartment = !filters.department || review.department === filters.department;
      const matchesDesignation = !filters.designation || review.designation === filters.designation;
      const matchesNominationStatus = !filters.reviewStatus || 
        (review.nominationStatus?.toLowerCase() === filters.reviewStatus.toLowerCase());
      const matchesOkrStatus = !filters.okrStatus || 
        (review.okrStatus?.toLowerCase() === filters.okrStatus.toLowerCase());

      return matchesSearch && matchesDepartment && matchesDesignation && matchesNominationStatus && matchesOkrStatus;
    });
  }, [reviews, searchTerm, filters.department, filters.designation, filters.reviewStatus, filters.okrStatus]);

  // Debug logging
  console.log('🔍 Debug - reviews array:', reviews);
  console.log('🔍 Debug - reviews length:', reviews.length);
  console.log('🔍 Debug - filteredReviews length:', filteredReviews.length);
  console.log('🔍 Debug - searchTerm:', searchTerm);
  console.log('🔍 Debug - filters:', filters);

  const totalItems = filteredReviews.length;
  const pageCount = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * (pageSize === 0 ? totalItems : pageSize);
  const endIndex = pageSize === 0 ? totalItems : Math.min(startIndex + pageSize, totalItems);
  const currentReviews = pageSize === 0 ? filteredReviews : filteredReviews.slice(startIndex, endIndex);

  // Handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (value: number) => {
    setPageSize(value);
    setPage(INITIAL_PAGE);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(INITIAL_PAGE);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleRemoveAllFilters = () => {
    const emptyFilters = {
      department: '',
      designation: '',
      reviewStatus: '',
      okrStatus: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSearchTerm('');
    setPage(INITIAL_PAGE);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowFilters(false);
    setPage(INITIAL_PAGE);
  };

  const handleOpenFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
    setTempFilters(filters);
  };

  // Navigation handler
  const handleViewDetails = (review: TeamReview) => {
    navigate(`/team-review/${review.nominationRelationshipId}`, {
      state: {
        nominationRelationshipId: review.nominationRelationshipId,
        employeeId: review.employeeId,
        employeeName: review.employeeName,
        designation: review.designation,
        department: review.department,
        employeeEmail: review.employeeEmail,
        managerName: review.managerName,
      }
    });
  };

  // Get unique values for filters - dynamically from API data
  const uniqueDepartments = [...new Set(reviews.map(r => r.department).filter(Boolean))].sort();
  const uniqueDesignations = [...new Set(reviews.map(r => r.designation).filter(Boolean))].sort();
  
  // Status options dynamically from API data
  const uniqueStatuses = [...new Set(reviews.map(r => r.nominationStatus).filter(Boolean))].sort() as string[];
  const uniqueOkrStatuses = [...new Set(reviews.map(r => r.okrStatus).filter(Boolean))].sort() as string[];

  const hasActiveFilters = filters.department || filters.designation || filters.reviewStatus || filters.okrStatus;

  // Helper function to convert status to title case
  const toTitleCase = (str: string | undefined | null) => {
    if (!str) return 'N/A';
    if (str.toUpperCase() === 'N/A') return 'N/A';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading team reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const { type, title, message } = deriveErrorState(error, {
      fallbackMessage: error,
      statusCode: errorStatus,
    });
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <ErrorState
          type={type}
          title={title}
          message={message}
          onRetry={handleRetry}
          retryLabel={loading ? 'Retrying...' : 'Try Again'}
          showRetryButton
        />
      </div>
    );
  }

  if (!user) {
    return (
      <ErrorState
        type="not-found"
        title="Authentication Required"
        message="No user data available. Please log in to continue."
        showRetryButton={false}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Section */}
      <div className="flex items-center gap-4">
        <div className="flex-grow" style={{ width: SEARCH_BAR_WIDTH }}>
          <SearchFilterBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onClearSearch={() => handleSearchChange('')}
            onOpenFilters={handleOpenFilters}
            hasActiveFilters={!!hasActiveFilters}
            activeFiltersCount={[filters.department, filters.designation, filters.reviewStatus, filters.okrStatus].filter(Boolean).length}
            tuneIcon={tuneIcon}
          />
        </div>
      </div>

      <FiltersModal
        show={showFilters}
        tempFilters={{
          department: tempFilters.department,
          designation: tempFilters.designation,
          nominationCapacity: tempFilters.reviewStatus,
          okrStatus: tempFilters.okrStatus
        }}
        uniqueDepartments={uniqueDepartments}
        uniqueDesignations={uniqueDesignations}
        uniqueNominationCapacities={uniqueStatuses}
        uniqueOkrStatuses={uniqueOkrStatuses}
        onChange={(filterType: string, value: string) => {
          // Map nominationCapacity back to reviewStatus
          const mappedFilterType = filterType === 'nominationCapacity' ? 'reviewStatus' : filterType;
          handleFilterChange(mappedFilterType, value);
        }}
        onApply={handleApplyFilters}
        onRemoveAll={handleRemoveAllFilters}
        onClose={handleCloseFilters}
        arrowDropDownIcon={arrowDropDownIcon}
        isTeamReviewPage={true}
      />

      {/* Table Card */}
      <Card>
        <div className="table-container">
          {/* Table Header */}
          <div className="bg-primary-800 rounded-lg px-6 py-3 border-b border-gray-200 mb-2">
            <div className="grid gap-4 text-left text-base font-medium text-white tracking-wider" 
                 style={{ gridTemplateColumns: 'minmax(80px, 110px) minmax(140px, 1fr) minmax(120px, 120px) minmax(100px, 140px) minmax(140px, 1fr) minmax(120px, 160px) minmax(100px, 130px)' }}>
              <div className="text-base">Employee ID</div>
              <div className="text-base">Employee Name</div>
              <div className="text-base">Designation</div>
              <div className="text-base">Department</div>
              <div className="text-base">Manager Name</div>
              <div className="text-base">GW 360 Feedback statuses</div>
              <div className="text-base">OKR Status</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-3 relative">
            {currentReviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Team Reviews Found</h3>
                <p className="text-gray-500 mb-4">
                  {hasActiveFilters 
                    ? 'No team reviews match your current search criteria.'
                    : 'No team reviews available.'
                  }
                </p>
              </div>
            ) : (
              currentReviews.map((review) => (
                <div 
                  key={review.id} 
                  className="hover:bg-gray-50 border border-gray-200 rounded-lg px-6 py-2 transition-colors relative cursor-pointer"
                  onClick={() => handleViewDetails(review)}
                >
                  <div className="grid gap-4 items-center" 
                       style={{ gridTemplateColumns: 'minmax(80px, 110px) minmax(140px, 1fr) minmax(120px, 120px) minmax(100px, 140px) minmax(140px, 1fr) minmax(120px, 160px) minmax(100px, 130px)' }}>
                    <div>
                      <div className="text-base text-gray-600 truncate">{review.employeeId}</div>
                    </div>
                    <div>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600 truncate"
                        name={review.employeeName}
                        email={review.employeeEmail}
                      />
                    </div>
                    <div>
                      <div className="text-base text-gray-600 truncate">{review.designation}</div>
                    </div>
                    <div>
                      <div className="text-base text-gray-600 truncate">{review.department}</div>
                    </div>
                    <div>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600 truncate"
                        name={review.managerName}
                        email={review.managerEmail}
                      />
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${review.nominationStatus ? getStatusBadgeClass(review.nominationStatus) : 'bg-gray-100 text-gray-800'}`}>
                        {toTitleCase(review.nominationStatus ==="approved"?"submitted":review.nominationStatus)}
                      </span>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${review.okrStatus ? getStatusBadgeClass(review.okrStatus) : 'bg-gray-100 text-gray-800'}`}>
                        {toTitleCase(review.okrStatus ==="approved"?"submitted":review.okrStatus)}
                      </span>
                    </div>

                    



                    
                    {/* <div className={`col-span-${ACTION_SPAN}`}>
                      <div className="flex items-center justify-center">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu(review.id, e);
                            }}
                            className="p-2 text-black hover:text-gray-600 focus:outline-none transition-colors rounded-full hover:bg-gray-100"
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          

                          {openMenuId === review.id && (
                            <div 
                              className={`absolute right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[${DROPDOWN_Z_INDEX}] ${
                                menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                <button 
                                  onClick={() => handleViewDetails(review)}
                                  className="flex items-center w-full px-4 py-2 text-base text-black hover:bg-gray-100"
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination - Only show when there are results */}
        {pageSize > 0 && totalItems > 0 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handleItemsPerPageChange}
              pageSizeOptions={PageSizeOptions}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default TeamReviewPageView;
